import { useEffect, Suspense, lazy } from 'react';
import { BrowserRouter, Routes, Route, useLocation, Navigate, useParams } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { ListingProvider, useListing } from './context/ListingContext';
import { FancoinProvider } from './context/FancoinContext';
import { CountryProvider } from './context/CountryContext';
import { Navbar } from './components/Navbar';
import { ScrollToTop } from './components/ScrollToTop';
import { ErrorBoundary } from './components/ErrorBoundary';

const SourcingSolutionsPage = lazy(() => import('./pages/SourcingSolutionsPage'));
import { PostListingModal } from './components/PostListingModal';
import SignInModal from './components/SignInModal';

const Footer = lazy(() => import('./components/Footer').then(m => ({ default: m.Footer })));
const BottomNav = lazy(() => import('./components/BottomNav').then(m => ({ default: m.BottomNav })));
const TrialBanner = lazy(() => import('./components/TrialBanner').then(m => ({ default: m.TrialBanner })));
const CookieConsent = lazy(() => import('./components/CookieConsent').then(m => ({ default: m.CookieConsent })));
const HomePage = lazy(() => import('./pages/HomePage').then((m) => ({ default: m.HomePage })));
const AllHubsPage = lazy(() => import('./pages/AllHubsPage').then((m) => ({ default: m.AllHubsPage })));
const ContactPage = lazy(() => import('./pages/ContactPage'));
const AboutPage = lazy(() => import('./pages/AboutPage'));
const HelpCenterPage = lazy(() => import('./pages/HelpCenterPage'));
const PrivacyRequestPage = lazy(() => import('./pages/PrivacyRequestPage'));
const TermsPage = lazy(() => import('./pages/TermsPage'));
const PrivacyPage = lazy(() => import('./pages/PrivacyPage'));
const CompliancePage = lazy(() => import('./pages/CompliancePage'));
const ListingRulesPage = lazy(() => import('./pages/ListingRulesPage'));
const AntiScamPage = lazy(() => import('./pages/AntiScamPage'));
const UserSafetyPage = lazy(() => import('./pages/UserSafetyPage'));
const ClassifiedsSafetyPage = lazy(() => import('./pages/ClassifiedsSafetyPage'));
const ReportListingPage = lazy(() => import('./pages/ReportListingPage'));
const ReportUserPage = lazy(() => import('./pages/ReportUserPage'));
const ReportSellerPolicyPage = lazy(() => import('./pages/ReportSellerPolicyPage'));
const VerifiedSellerPolicyPage = lazy(() => import('./pages/VerifiedSellerPolicyPage'));
const HubPage = lazy(() => import('./pages/HubPage').then((m) => ({ default: m.HubPage })));
const CategoryLandingPage = lazy(() => import('./pages/CategoryLandingPage').then((m) => ({ default: m.CategoryLandingPage })));

const CreateListingPage = lazy(() =>
  import('./pages/CreateListingPage').then((m) => ({ default: m.CreateListingPage }))
);
const PamboLivePage = lazy(() =>
  import('./pages/PamboLivePage').then((m) => ({ default: m.PamboLivePage }))
);
const SellerPage = lazy(() =>
  import('./pages/SellerPage').then((m) => ({ default: m.SellerPage }))
);
const ListingDetailPage = lazy(() =>
  import('./pages/ListingDetailPage').then((m) => ({ default: m.ListingDetailPage }))
);
const DashboardPage = lazy(() =>
  import('./pages/DashboardPage').then((m) => ({ default: m.DashboardPage }))
);
const SearchPage = lazy(() =>
  import('./pages/SearchPage').then((m) => ({ default: m.SearchPage }))
);

import { analyticsService } from './services/analyticsService';
import { HelmetProvider } from 'react-helmet-async';

const PricingPage = lazy(() => import('./pages/PricingPage').then((m) => ({ default: m.PricingPage })));
const AdminDashboardPage = lazy(() => import('./pages/AdminDashboardPage'));
const SafeTradingPage = lazy(() => import('./pages/SafeTradingPage'));
const LoyaltyPage = lazy(() => import('./pages/LoyaltyPage'));
const CareersPage = lazy(() => import('./pages/CareersPage'));
const SellerSuccessPage = lazy(() => import('./pages/SellerSuccessPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const MerchantVerificationPage = lazy(() => import('./pages/MerchantVerificationPage'));
const SavedAdsPage = lazy(() => import('./pages/SavedAdsPage').then((m) => ({ default: m.SavedAdsPage })));
const MessagesPage = lazy(() => import('./pages/MessagesPage').then((m) => ({ default: m.MessagesPage })));
const NotificationsPage = lazy(() => import('./pages/NotificationsPage'));
const PWAReloadPrompt = lazy(() => import('./components/PWAReloadPrompt').then(m => ({ default: m.PWAReloadPrompt })));
const InstallPWABanner = lazy(() => import('./components/InstallPWABanner').then(m => ({ default: m.InstallPWABanner })));
const LiveNotificationWatcher = lazy(() => import('./components/LiveNotificationWatcher').then(m => ({ default: m.LiveNotificationWatcher })));

export default function App() {
  return (
    <HelmetProvider>
      <BrowserRouter>
        <CountryProvider>
          <AuthProvider>
            <FancoinProvider>
              <ListingProvider>
                <ErrorBoundary>
                  <AppContent />
                </ErrorBoundary>
              </ListingProvider>
            </FancoinProvider>
          </AuthProvider>
        </CountryProvider>
      </BrowserRouter>
    </HelmetProvider>
  );
}

function AppContent() {
  const location = useLocation();
  const { isPostListingModalOpen, closePostListingModal } = useListing();
  const { user, isAuthModalOpen, closeAuthModal, authModalTab } = useAuth();

  useEffect(() => {
    analyticsService.logPageView(location.pathname);
  }, [location]);

  useEffect(() => {
    // --- CAPTURE REFERRAL ID ---
    const urlParams = new URLSearchParams(window.location.search);
    const ref = urlParams.get('ref');
    if (ref) {
      sessionStorage.setItem('pambo_referral_id', ref);
      // Clean up URL to keep it pretty
      const newUrl = window.location.pathname;
      window.history.replaceState({}, '', newUrl);
    }

    // --- FORCE UPDATE CHECK ON NAVIGATION ---
    if ('serviceWorker' in navigator) {
      navigator.serviceWorker.getRegistrations().then(regs => {
        for (const reg of regs) {
          reg.update(); // Forces the browser to check for sw.js changes instantly
        }
      });
    }
  }, [location]);

  return (
    <div className="min-h-screen bg-gray-50 font-sans text-gray-900 flex flex-col">
      <a
        href="#main-content"
        className="sr-only focus:not-sr-only focus:fixed focus:top-4 focus:left-4 focus:z-200 focus:bg-orange-600 focus:text-white focus:px-4 focus:py-2 focus:rounded-lg"
      >
        Skip to main content
      </a>
      <ScrollToTop />
      <Navbar />
      <Suspense fallback={null}>
        <TrialBanner />
        {user && <LiveNotificationWatcher />}
      </Suspense>
      <div id="main-content" className="flex-1 pb-20 lg:pb-0">
        <Suspense fallback={<div className="min-h-screen" />}>
          <Routes>
            <Route path="/" element={<HomePage />} />
            <Route path="/hubs" element={<AllHubsPage />} />
            <Route path="/hub/hotels/product/:listingId" element={<ListingDetailPage />} />
            <Route path="/hub/:hubId" element={<HubPage />} />
            <Route path="/category/:slug" element={<CategoryLandingPage />} />

            {/* Top-Level SEO Trade Hubs */}
            <Route path="/hotels" element={<HubPage overrideHubId="hotels" overrideSeoSlug="hotels" />} />
            <Route path="/logistics" element={<HubPage overrideHubId="logistics" overrideSeoSlug="logistics" />} />
            <Route path="/retailers" element={<HubPage overrideHubId="retailers" overrideSeoSlug="retailers" />} />
            <Route path="/wholesale" element={<HubPage overrideHubId="wholesalers" overrideSeoSlug="wholesale" />} />
            <Route path="/secondhand" element={<HubPage overrideHubId="secondhand" overrideSeoSlug="secondhand" />} />
            <Route path="/farmer-produce" element={<HubPage overrideHubId="farmer" overrideSeoSlug="farmer-produce" />} />
            <Route path="/digital-products" element={<HubPage overrideHubId="digital" overrideSeoSlug="digital-products" />} />
            <Route path="/services" element={<HubPage overrideHubId="services" overrideSeoSlug="services" />} />
            <Route path="/tech-electronics" element={<HubPage overrideHubId="tech" overrideSeoSlug="tech-electronics" />} />
            <Route path="/cars" element={<HubPage overrideHubId="cars" overrideSeoSlug="cars" />} />
            <Route path="/real-estate" element={<HubPage overrideHubId="real-estate" overrideSeoSlug="real-estate" />} />
            <Route path="/fashion" element={<HubPage overrideHubId="fashion" overrideSeoSlug="fashion" />} />
            <Route path="/kitchen-equipment" element={<HubPage overrideHubId="kitchen-equipment" overrideSeoSlug="kitchen-equipment" />} />

            {/* Legacy redirect for old product links */}
            <Route
              path="/hotels/product/:listingId"
              element={<Navigate to="/hub/hotels" replace />}
            />
            <Route path="/seller/:identifier/:slug?" element={<SellerPage />} />
            <Route path="/shop/:identifier/:slug?" element={<SellerPage />} />
            <Route
              path="/seller/:identifier/post-listing"
              element={<Navigate to="/post-listing" replace />}
            />
            <Route path="/create-listing" element={<Navigate to="/post-listing" replace />} />
            <Route path="/post-listing" element={<CreateListingPage />} />
            <Route path="/listing/:listingId/:slug?" element={<ListingDetailPage />} />
            <Route path="/messages" element={<MessagesPage />} />
            <Route path="/notifications" element={<NotificationsPage />} />
            <Route path="/dashboard" element={<DashboardPage />} />
            <Route path="/saved" element={<SavedAdsPage />} />
            <Route path="/search" element={<SearchPage />} />

            <Route path="/pricing" element={<PricingPage />} />
            <Route path="/contact" element={<ContactPage />} />
            <Route path="/about" element={<AboutPage />} />
            <Route path="/help-center" element={<HelpCenterPage />} />
            <Route path="/signup" element={<LoginRedirect defaultTab="signup" />} />
            <Route path="/login" element={<LoginRedirect defaultTab="login" />} />
            <Route path="/sourcing-solutions" element={<SourcingSolutionsPage />} />
            <Route path="/safe-trading" element={<SafeTradingPage />} />
            <Route path="/terms" element={<TermsPage />} />
            <Route path="/privacy" element={<PrivacyPage />} />
            <Route path="/listing-rules" element={<ListingRulesPage />} />
            <Route path="/anti-scam" element={<AntiScamPage />} />
            <Route path="/user-safety" element={<UserSafetyPage />} />
            <Route path="/safety-guide" element={<ClassifiedsSafetyPage />} />
            <Route path="/report-listing" element={<ReportListingPage />} />
            <Route path="/report-user" element={<ReportUserPage />} />
            <Route path="/report-seller-policy" element={<ReportSellerPolicyPage />} />
            <Route path="/verified-seller-policy" element={<VerifiedSellerPolicyPage />} />
            <Route path="/compliance" element={<CompliancePage />} />
            <Route path="/privacy-request" element={<PrivacyRequestPage />} />
            <Route path="/admin" element={<AdminDashboardPage />} />
            <Route path="/live" element={<PamboLivePage />} />
            <Route path="/post-ad" element={<Navigate to="/post-listing" replace />} />
            <Route path="/loyalty" element={<LoyaltyPage />} />
            <Route path="/careers" element={<CareersPage />} />
            <Route path="/seller-success" element={<SellerSuccessPage />} />
            <Route path="/settings" element={<SettingsPage />} />
            <Route path="/merchant-verification" element={<MerchantVerificationPage />} />

            {/* Footer Link Aliases / Redirects */}
            <Route path="/create-ad" element={<Navigate to="/post-listing" replace />} />
            <Route path="/rfq" element={<Navigate to="/hub/global-wholesalers" replace />} />
            <Route path="/affiliate" element={<Navigate to="/loyalty" replace />} />
            <Route path="/promote" element={<Navigate to="/pricing" replace />} />
            <Route path="/verify" element={<Navigate to="/merchant-verification" replace />} />
            <Route path="/store" element={<Navigate to="/dashboard" replace />} />
            <Route path="/hub" element={<Navigate to="/contact?reason=hub-hosting" replace />} />
            <Route path="/make-money" element={<Navigate to="/dashboard" replace />} />
            <Route path="/seller-hub" element={<Navigate to="/dashboard" replace />} />
          </Routes>
        </Suspense>
      </div>
      <Suspense fallback={null}>
        <BottomNav />
        <Footer />
        <CookieConsent />
        <PWAReloadPrompt />
        <InstallPWABanner />
        {isPostListingModalOpen && <PostListingModal isOpen={isPostListingModalOpen} onClose={closePostListingModal} />}
        {isAuthModalOpen && <SignInModal isOpen={isAuthModalOpen} onClose={closeAuthModal} defaultTab={authModalTab} />}
      </Suspense>
    </div>
  );
}

function CategoryRedirect() {
  const { hubId } = useParams();
  return <Navigate to={`/hub/${hubId}`} replace />;
}

function LoginRedirect({ defaultTab }: { defaultTab: 'login' | 'signup' }) {
  const { openAuthModal } = useAuth();
  useEffect(() => {
    openAuthModal(defaultTab);
  }, [openAuthModal, defaultTab]);
  return <Navigate to="/" replace />;
}
